import React from "react";
import { useNavigate, useLocation } from "react-router-dom";

// ---------------------------------------------------------------------------
// Hotel e-Ticket (voucher)
// Data: HotelReview.jsx payment ke baad `ticketData` banata hai aur
//   navigate("/hotel-ticket", { state: { ticketData } }) + sessionStorage("hotelTicketData")
// ---------------------------------------------------------------------------

const readTicketData = () => {
  try {
    return JSON.parse(sessionStorage.getItem("hotelTicketData") || "null");
  } catch (e) {
    return null;
  }
};

const fmtDate = (d) => {
  if (!d || d === "—") return "-";
  const parsed = new Date(d);
  if (!isNaN(parsed)) {
    return parsed.toLocaleDateString("en-IN", {
      weekday: "long",
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  }
  return d;
};

const inr = (n) => {
  if (n == null || n === "") return "-";
  return `₹${Number(n).toLocaleString("en-IN")}`;
};

export default function HotelTicket() {
  const navigate = useNavigate();
  const location = useLocation();

  // Fallback: agar ticketData nahi bana (payment-success se redirect hua),
  // to sessionStorage ki purani keys se ticket data banao.
  const buildFromStorage = () => {
    const read = (k) => {
      try {
        return JSON.parse(sessionStorage.getItem(k) || "null");
      } catch (e) {
        return null;
      }
    };
    const rd = read("hotelReviewData");
    if (!rd) return null;
    const sp = read("hotelSearchPayload") || {};
    const g = read("guestDetails") || {};
    const r = Array.isArray(rd.HotelRooms) && rd.HotelRooms.length > 0 ? rd.HotelRooms[0] : {};
    const base = Number(r.totalPrice || 0);
    return {
      bookingId:
        new URLSearchParams(location.search).get("bookingId") ||
        sessionStorage.getItem("lastBookingId") ||
        sessionStorage.getItem("hotelBookingId") ||
        "",
      paymentId: sessionStorage.getItem("lastPaymentId") || "",
      orderId: sessionStorage.getItem("lastRazorpayOrderId") || "",
      amount: Number(sessionStorage.getItem("lastAmount")) || base,
      currency: "INR",
      paidAt: new Date().toISOString(),
      hotel: {
        name: rd.hotelname || "",
        address: [rd.HotelAddress, rd.CityName, rd.StateName, rd.CountryName].filter(Boolean).join(", "),
        rating: rd.HotelRating || 0,
      },
      stay: {
        checkIn: sp.CheckIn,
        checkOut: sp.CheckOut,
        nights: Number(sp.Night || 1),
        adults: Number(r.Adults || sp.Numofadults || 1),
      },
      room: { type: r.RoomType || "", meal: r.Mealbasis || "" },
      guest: {
        title: g.title,
        firstName: g.firstName,
        lastName: g.lastName,
        email: g.email,
        countryCode: g.countryCode,
        mobile: g.mobile,
        gstNumber: g.gstEnabled ? g.gstNumber : "",
      },
      price: { base, taxes: 0, total: base },
    };
  };

  const t = location.state?.ticketData || readTicketData() || buildFromStorage();

  if (!t) {
    return (
      <div style={S.page}>
        <div style={S.noDataBox}>
          <div style={{ fontSize: 40 }}>⚠️</div>
          <h2 style={S.h2}>No booking data found</h2>
          <p style={S.muted}>Please complete booking first.</p>
          <button style={S.btnBlue} onClick={() => navigate("/")}>Go Home</button>
        </div>
      </div>
    );
  }

  const hotel = t.hotel || {};
  const stay = t.stay || {};
  const room = t.room || {};
  const guest = t.guest || {};
  const price = t.price || {};

  const stars = Math.max(0, Math.min(5, Number(hotel.rating) || 0));
  const nights = Number(stay.nights || 1);
  const adults = Number(stay.adults || 1);

  const bookingDateDisplay = (t.paidAt ? new Date(t.paidAt) : new Date()).toLocaleDateString("en-IN", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  // ticketData sirf payment verify hone ke baad banta hai -> Confirmed
  const statusLabel = t.paymentId ? "Confirmed" : "Pending";
  const statusStyle = t.paymentId ? S.badgeGreen : S.badgeYellow;

  const guestName = `${guest.title || ""} ${guest.firstName || ""} ${guest.lastName || ""}`.trim() || "-";
  const guestMobile = guest.mobile ? `${guest.countryCode || "+91"} ${guest.mobile}` : "-";

  const basePrice = Number(price.base || 0);
  const taxes = Number(price.taxes || 0);
  const total = Number(t.amount ?? price.total ?? basePrice + taxes);

  return (
    <div style={S.page} className="ticket-page">
      <div style={S.printable} className="ticket-printable">
        <table style={S.outerTable}>
          <tbody>
            {/* ══════════════ HEADER ══════════════ */}
            <tr>
              <td style={{ padding: "16px 20px 0" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <tbody>
                    <tr>
                      <td style={{ width: "20%", verticalAlign: "middle" }}>
                        <img src="/tripcaliber-logo.png" alt="Logo" style={{ width: 160 }} />
                      </td>
                      <td style={{ textAlign: "right", fontSize: 11, color: "#333", verticalAlign: "middle" }}>
                        <strong style={{ fontSize: 18 }}>TripCaliber</strong>
                        <p style={{ margin: "5px 0", fontSize: 11 }}>
                          <strong>Booking Date:</strong> {bookingDateDisplay}
                        </p>
                      </td>
                    </tr>
                    <tr>
                      <td colSpan={2}>
                        <hr style={{ borderColor: "#d1d1d1", margin: "10px 0" }} />
                      </td>
                    </tr>
                    <tr>
                      <td style={{ fontSize: 11, color: "#333" }}>
                        <strong>Your Hotel e-Ticket</strong>
                      </td>
                      <td style={{ textAlign: "right", fontSize: 11, color: "#333" }}>
                        <strong>Booking Date:</strong> {bookingDateDisplay}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </td>
            </tr>

            {/* ══════════════ BOOKING INFO + BARCODE ══════════════ */}
            <tr>
              <td style={{ padding: "16px 20px" }}>
                <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
                  <tbody>
                    <tr>
                      <td style={{ width: "50%", verticalAlign: "top" }}>
                        <table cellPadding={3}>
                          <tbody>
                            <tr>
                              <td style={{ width: 120 }}><strong>Reference ID</strong></td>
                              <td>{t.bookingId || "-"}</td>
                            </tr>
                            <tr>
                              <td><strong>Booking Status</strong></td>
                              <td>
                                <span style={{ ...S.statusBadge, ...statusStyle }}>{statusLabel}</span>
                              </td>
                            </tr>
                            {t.paymentId && (
                              <tr>
                                <td><strong>Payment ID</strong></td>
                                <td>{t.paymentId}</td>
                              </tr>
                            )}
                            {t.orderId && (
                              <tr>
                                <td><strong>Order ID</strong></td>
                                <td>{t.orderId}</td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </td>
                      {/* <td style={{ width: "50%", textAlign: "right", verticalAlign: "middle" }}>
                        <div style={S.barcodeBox}>
                          <div style={S.barcodeLines}>
                            {Array.from({ length: 30 }).map((_, i) => (
                              <div key={i} style={{ ...S.barcodeLine, width: i % 3 === 0 ? 3 : 1.5 }} />
                            ))}
                          </div>
                          <div style={{ fontSize: 10, color: "#555", marginTop: 4, letterSpacing: 2 }}>
                            {t.bookingId}
                          </div>
                        </div>
                      </td> */}
                    </tr>
                  </tbody>
                </table>
              </td>
            </tr>

            {/* ══════════════ HOTEL DETAILS ══════════════ */}
            <tr>
              <td style={{ padding: "0 20px 16px" }}>
                <hr style={{ borderStyle: "dashed", borderColor: "#ccc", margin: "8px 0 16px" }} />
                <strong style={{ color: "#1f91cd", fontSize: 15 }}>Hotel - Details</strong>

                <table style={{ ...S.dataTable, width: "100%", marginTop: 8 }}>
                  <thead>
                    <tr>
                      {["Hotel", "Address", "Check-in", "Check-out", "Nights"].map((h) => (
                        <th key={h} style={S.th}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={S.td}>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{hotel.name || "-"}</div>
                        {stars > 0 && (
                          <div style={{ color: "#f59e0b", fontSize: 12, letterSpacing: 1 }}>{"★".repeat(stars)}</div>
                        )}
                      </td>
                      <td style={S.td}>{hotel.address || "-"}</td>
                      <td style={S.td}>{fmtDate(stay.checkIn)}</td>
                      <td style={S.td}>{fmtDate(stay.checkOut)}</td>
                      <td style={{ ...S.td, textAlign: "center" }}>{nights}</td>
                    </tr>
                  </tbody>
                </table>

                <table style={{ ...S.dataTable, width: "100%", marginTop: 12 }}>
                  <thead>
                    <tr>
                      {["Room Type", "Meal Basis", "Adults"].map((h) => (
                        <th key={h} style={S.th}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={S.td}>{room.type || "-"}</td>
                      <td style={S.td}>{room.meal || "-"}</td>
                      <td style={{ ...S.td, textAlign: "center" }}>{adults}</td>
                    </tr>
                  </tbody>
                </table>
              </td>
            </tr>

            {/* ══════════════ GUEST DETAILS ══════════════ */}
            <tr>
              <td style={{ padding: "0 20px 16px" }}>
                <strong style={{ color: "#1f91cd", fontSize: 15 }}>Guest - Details</strong>

                <table style={{ ...S.dataTable, width: "100%", marginTop: 8 }}>
                  <thead>
                    <tr>
                      {["Guest Name", "Email", "Mobile", "GST No.", "Status"].map((h) => (
                        <th key={h} style={S.th}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={S.td}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <div style={S.avatar}>{guest.firstName?.[0]?.toUpperCase() || "G"}</div>
                          {guestName}
                        </div>
                      </td>
                      <td style={S.td}>{guest.email || "-"}</td>
                      <td style={S.td}>{guestMobile}</td>
                      <td style={S.td}>{guest.gstNumber || "-"}</td>
                      <td style={S.td}>{statusLabel}</td>
                    </tr>
                  </tbody>
                </table>
              </td>
            </tr>

            {/* ══════════════ FARE DETAILS ══════════════ */}
            <tr>
              <td style={{ padding: "0 20px 16px" }}>
                <table style={{ ...S.fareTable, width: "100%" }}>
                  <tbody>
                    <tr>
                      <td style={{ padding: "6px 8px" }}>
                        <strong style={{ fontSize: 15, color: "#1f91cd" }}>Fare Details</strong>
                      </td>
                      <td style={{ padding: "6px 8px" }} />
                      <td style={{ padding: "6px 8px", textAlign: "right" }}>
                        <strong style={{ fontSize: 15, color: "#1f91cd" }}>Amount (INR)</strong>
                      </td>
                    </tr>
                    <tr>
                      <td style={S.fareTd}>Room Charges ({nights} night{nights > 1 ? "s" : ""}) :</td>
                      <td style={S.fareTd} />
                      <td style={{ ...S.fareTd, textAlign: "right" }}>{inr(basePrice)}</td>
                    </tr>
                    <tr>
                      <td style={S.fareTd}>Taxes &amp; Fees :</td>
                      <td style={S.fareTd} />
                      <td style={{ ...S.fareTd, textAlign: "right" }}>{inr(taxes)}</td>
                    </tr>
                    <tr>
                      <td colSpan={3} style={{ padding: 0 }}>
                        <hr style={{ borderColor: "#ccc", margin: "4px 0" }} />
                      </td>
                    </tr>
                    <tr>
                      <td style={{ ...S.fareTd, fontWeight: 700, fontSize: 14 }}>Total Paid</td>
                      <td style={S.fareTd} />
                      <td style={{ ...S.fareTd, fontWeight: 700, fontSize: 14, textAlign: "right" }}>
                        {inr(total)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </td>
            </tr>

            {/* ══════════════ TERMS & CONDITIONS ══════════════ */}
            <tr>
              <td style={{ padding: "0 20px 20px", fontSize: 11, fontFamily: "Tahoma, Geneva, sans-serif", color: "#5f6061" }}>
                <strong>Terms &amp; Conditions</strong>
                <ul style={{ marginTop: 8, paddingLeft: 20 }}>
                  {[
                    "Guests must carry a valid government photo ID (Aadhaar, Passport, Driving Licence, Voter ID) at check-in.",
                    "Standard check-in time is usually 12:00 PM and check-out time 11:00 AM, unless the hotel specifies otherwise.",
                    "Early check-in or late check-out is subject to availability and may be charged by the hotel.",
                    "Foreign nationals must carry their passport and a valid visa.",
                    "Please present this e-ticket (printed or on your phone) at the hotel reception.",
                    "For cancellation / refund policy, please refer to the hotel's rules at the time of booking.",
                  ].map((x, i) => (
                    <li key={i} style={{ marginBottom: 4 }}>{x}</li>
                  ))}
                </ul>
              </td>
            </tr>
          </tbody>
        </table>

        {/* ══════════════ ACTION BUTTONS (no print) ══════════════ */}
        <div style={S.actions} className="noPrint">
          <button style={S.btnBlue} onClick={() => window.print()}>🖨️ Print Ticket</button>
          <button style={{ ...S.btnBlue, background: "#6c757d" }} onClick={() => navigate("/")}>
            🏠 Home
          </button>
        </div>
      </div>

      <style>{`
        @media print {
          .noPrint { display: none !important; }
          .ticket-page { background: #fff !important; padding: 0 !important; margin: 0 !important; }
          .ticket-printable { max-width: 100% !important; width: 100% !important; margin: 0 !important; }
          .ticket-printable * { font-size: 11px !important; }
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
          table, tr, td, th { page-break-inside: avoid; }
          .ticket-printable table { break-inside: avoid; }
          @page { size: A4; margin: 8mm; }
        }
      `}</style>
    </div>
  );
}

// ============================================================
// STYLES (flight ticket jaise hi)
// ============================================================
const S = {
  page: {
    minHeight: "100vh",
    background: "#e6f0ff",
    padding: "1.5rem 1rem",
    fontFamily: "Tahoma, Geneva, sans-serif",
  },
  printable: { maxWidth: 1100, margin: "0 auto" },
  outerTable: {
    border: "1px solid #d1d1d1",
    fontFamily: "Tahoma, Geneva, sans-serif",
    borderRadius: 4,
    background: "#fff",
    width: "100%",
    borderCollapse: "collapse",
  },
  statusBadge: {
    display: "inline-block",
    padding: "3px 10px",
    borderRadius: 12,
    fontSize: 11,
    fontWeight: 600,
  },
  badgeGreen: { background: "#dcfce7", color: "#15803d" },
  badgeYellow: { background: "#fef9c3", color: "#a16207" },
  barcodeBox: {
    display: "inline-block",
    padding: "8px 12px",
    border: "1px solid #eee",
    borderRadius: 4,
  },
  barcodeLines: { display: "flex", alignItems: "stretch", gap: 2, height: 60 },
  barcodeLine: { background: "#222", height: "100%", borderRadius: 1 },
  dataTable: { borderCollapse: "collapse", fontSize: 12, marginTop: 5 },
  th: {
    background: "#bedff1",
    border: "1px solid #111",
    padding: "6px 8px",
    fontWeight: 700,
    fontSize: 12,
    whiteSpace: "nowrap",
  },
  td: {
    border: "1px solid #111",
    padding: "6px 8px",
    fontSize: 12,
    verticalAlign: "middle",
  },
  avatar: {
    width: 26,
    height: 26,
    borderRadius: "50%",
    background: "#eff6ff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 11,
    fontWeight: 700,
    color: "#2563eb",
    flexShrink: 0,
  },
  fareTable: { border: "1px solid #d1d1d1", borderCollapse: "collapse", fontSize: 12 },
  fareTd: { padding: "5px 8px", fontSize: 13 },
  actions: { display: "flex", gap: 10, justifyContent: "center", padding: "1rem 0" },
  btnBlue: {
    backgroundColor: "#007BFF",
    color: "#fff",
    border: "none",
    padding: "10px 20px",
    borderRadius: 4,
    cursor: "pointer",
    fontSize: 14,
    fontWeight: 600,
  },
  noDataBox: {
    maxWidth: 480,
    margin: "60px auto",
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    padding: "2rem",
    textAlign: "center",
  },
  h2: { fontSize: 20, fontWeight: 700, color: "#111827", margin: "8px 0" },
  muted: { fontSize: 14, color: "#6b7280" },
};  